import React, { useState, useEffect } from 'react'
import { X, User, Heart, Calendar, MessageCircle, Sparkles, Clock } from 'lucide-react'
import playCreateStorageService from '../services/playCreateStorageService'

export const CharacterProfileModal = ({ character, onClose }) => {
  const [characterState, setCharacterState] = useState(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    const loadState = async () => {
      try {
        setIsLoading(true)
        const state = await playCreateStorageService.getCharacterState(character.id)
        setCharacterState(state)
      } catch (error) {
        console.error('❌ 加载角色状态失败:', error)
      } finally {
        setIsLoading(false)
      }
    }

    if (character) {
      loadState()
    }
  }, [character])

  if (!character) return null

  const formatDate = (timestamp) => {
    if (!timestamp) return '未知'
    return new Date(timestamp).toLocaleString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const getFavorabilityLevel = (fav) => {
    if (fav < 20) return { text: '陌生', color: 'text-gray-400', bgColor: 'bg-gray-500' }
    if (fav < 50) return { text: '熟悉', color: 'text-blue-400', bgColor: 'bg-blue-500' }
    if (fav < 80) return { text: '友好', color: 'text-green-400', bgColor: 'bg-green-500' }
    return { text: '亲密', color: 'text-pink-400', bgColor: 'bg-pink-500' }
  }

  const daysTogether = characterState?.firstChatTime
    ? playCreateStorageService.calculateDaysTogether(characterState.firstChatTime)
    : 0

  const level = getFavorabilityLevel(characterState?.favorability || 0)

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-dark-800 rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto border border-dark-600">
        {/* 头部 */}
        <div className="sticky top-0 bg-dark-800 border-b border-dark-600 p-6 z-10">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold text-white flex items-center gap-2">
              <User size={24} className="text-primary" />
              角色档案
            </h2>
            <button
              onClick={onClose}
              className="text-dark-400 hover:text-white transition-colors"
            >
              <X size={24} />
            </button>
          </div>
        </div>

        {isLoading ? (
          <div className="p-6 text-center text-dark-400">加载中...</div>
        ) : (
          <div className="p-6 space-y-6">
            {/* 角色头像和基本信息 */}
            <div className="flex flex-col items-center">
              <div className="w-32 h-32 rounded-full overflow-hidden bg-dark-700 border-4 border-dark-600 mb-4">
                {character.avatar ? (
                  <img
                    src={character.avatar}
                    alt={character.name}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(character.name)}&background=random&size=256`
                    }}
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-primary to-secondary text-white text-4xl font-bold">
                    {character.name?.charAt(0) || '?'}
                  </div>
                )}
              </div>

              <h3 className="text-3xl font-bold text-white mb-2">{character.name}</h3>
              <div className={`px-4 py-1 rounded-full text-sm font-medium bg-dark-700 border ${level.color} border-current`}>
                {level.text}
              </div>
            </div>

            {/* 好感度进度条 */}
            <div className="bg-dark-700 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Heart size={18} className={level.color} />
                  <span className="text-white font-medium">好感度</span>
                </div>
                <span className={`text-lg font-bold ${level.color}`}>
                  {characterState?.favorability || 0}/100
                </span>
              </div>
              <div className="w-full h-3 bg-dark-600 rounded-full overflow-hidden">
                <div
                  className={`h-full transition-all duration-300 ${level.bgColor}`}
                  style={{ width: `${characterState?.favorability || 0}%` }}
                />
              </div>
            </div>

            {/* 统计信息 */}
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-dark-700 rounded-lg p-4">
                <div className="flex items-center gap-2 text-dark-400 mb-2">
                  <Calendar size={16} />
                  <span className="text-sm">相处天数</span>
                </div>
                <div className="text-2xl font-bold text-white">{daysTogether} 天</div>
              </div>

              <div className="bg-dark-700 rounded-lg p-4">
                <div className="flex items-center gap-2 text-dark-400 mb-2">
                  <MessageCircle size={16} />
                  <span className="text-sm">对话次数</span>
                </div>
                <div className="text-2xl font-bold text-white">
                  {characterState?.totalChatCount || 0} 次
                </div>
              </div>
            </div>

            {/* 角色描述 */}
            <div className="bg-dark-700 rounded-lg p-4">
              <h4 className="text-white font-medium mb-2 flex items-center gap-2">
                <Sparkles size={16} className="text-primary" />
                角色描述
              </h4>
              <p className="text-dark-300 text-sm leading-relaxed">
                {character.description || '暂无描述'}
              </p>
            </div>

            {/* 性格特点 */}
            <div className="bg-dark-700 rounded-lg p-4">
              <h4 className="text-white font-medium mb-2 flex items-center gap-2">
                <User size={16} className="text-secondary" />
                性格特点
              </h4>
              <p className="text-dark-300 text-sm leading-relaxed">
                {character.personality || '暂无性格描述'}
              </p>
            </div>

            {/* 时间信息 */}
            <div className="bg-dark-700 rounded-lg p-4 space-y-2">
              <h4 className="text-white font-medium mb-3 flex items-center gap-2">
                <Clock size={16} className="text-blue-400" />
                时间记录
              </h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-dark-400">创建时间</span>
                  <span className="text-white">{formatDate(character.createdAt)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-dark-400">首次聊天</span>
                  <span className="text-white">{formatDate(characterState?.firstChatTime)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-dark-400">最后聊天</span>
                  <span className="text-white">{formatDate(characterState?.lastChatTime)}</span>
                </div>
              </div>
            </div>

            {/* 优化后的人设信息（如果有） */}
            {character.optimizedPersonality && (
              <div className="bg-dark-700 rounded-lg p-4">
                <h4 className="text-white font-medium mb-3 flex items-center gap-2">
                  <Sparkles size={16} className="text-primary" />
                  AI优化人设
                </h4>
                <div className="space-y-3 text-sm">
                  {character.optimizedPersonality.keyTraits && character.optimizedPersonality.keyTraits.length > 0 && (
                    <div>
                      <span className="text-dark-400">关键特征：</span>
                      <div className="flex flex-wrap gap-2 mt-1">
                        {character.optimizedPersonality.keyTraits.map((trait, index) => (
                          <span key={index} className="px-2 py-1 bg-primary/20 text-primary rounded text-xs">
                            {trait}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  {character.optimizedPersonality.interests && character.optimizedPersonality.interests.length > 0 && (
                    <div>
                      <span className="text-dark-400">兴趣爱好：</span>
                      <div className="flex flex-wrap gap-2 mt-1">
                        {character.optimizedPersonality.interests.map((interest, index) => (
                          <span key={index} className="px-2 py-1 bg-secondary/20 text-secondary rounded text-xs">
                            {interest}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  {character.optimizedPersonality.speechStyle && (
                    <div>
                      <span className="text-dark-400">说话风格：</span>
                      <span className="text-white ml-2">{character.optimizedPersonality.speechStyle}</span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* 底部按钮 */}
        <div className="sticky bottom-0 bg-dark-800 border-t border-dark-600 p-6">
          <button
            onClick={onClose}
            className="w-full px-4 py-3 bg-primary hover:bg-primary/80 text-white rounded-lg transition-colors font-medium"
          >
            关闭
          </button>
        </div>
      </div>
    </div>
  )
}

